import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DesktopDateTimePicker } from '@mui/x-date-pickers';
import { Controller, useFormContext } from 'react-hook-form';
import { RHFSelect } from 'src/components/hook-form';
import { translateKey } from 'src/locales/translate';
import { CreateReminderDtoRecurrenceTypeEnum } from 'woofwoof-api';
import MedicationReminderRecurrence from './MedicationReminderRecurrence';
import { SPAN_FULL, SPAN_WIDE } from './medicationFormLayout';
import { selectTestId } from './selectTestId';

const RECURRENCE_OPTIONS = [
  { value: CreateReminderDtoRecurrenceTypeEnum.Single, label: 'Jednorazowo' },
  { value: CreateReminderDtoRecurrenceTypeEnum.Daily, label: 'Codziennie' },
  {
    value: CreateReminderDtoRecurrenceTypeEnum.Monthly,
    label: translateKey('hardcoded.miesiecznie'),
  },
  { value: CreateReminderDtoRecurrenceTypeEnum.Yearly, label: 'Rocznie' },
  {
    value: CreateReminderDtoRecurrenceTypeEnum.Custom,
    label: 'Niestandardowe',
  },
];

interface MedicationReminderSectionProps {
  canEditReminder: boolean;
  singleReminderOnly?: boolean;
}

/**
 * Lives inside the form's grid rather than in a stack of its own, so the
 * reminder fields sit beside each other on a wide dialog instead of adding
 * another screen of height.
 */
export default function MedicationReminderSection({
  canEditReminder,
  singleReminderOnly = false,
}: MedicationReminderSectionProps) {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();
  const reminderEnabled = watch('reminderEnabled');
  const reminderRecurrenceType = watch('reminderRecurrenceType');
  // The form stays on screen with the toggle off, so a vet can see what a
  // reminder would ask for before deciding to set one.
  const fieldsDisabled = !canEditReminder || !reminderEnabled;

  return (
    <>
      <Controller
        name="reminderEnabled"
        control={control}
        render={({ field }) => (
          <Stack direction="row" alignItems="center" spacing={1} sx={SPAN_FULL}>
            <Typography>
              {canEditReminder
                ? translateKey('visit.visitCourseMenuReminder')
                : translateKey('hardcoded.przypomnienie_podglad')}
            </Typography>
            <Switch
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              disabled={!canEditReminder}
            />
          </Stack>
        )}
      />

      <Box sx={SPAN_WIDE}>
        <Controller
          name="reminderDescription"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Opis przypomnienia"
              multiline
              rows={2}
              required
              fullWidth
              disabled={fieldsDisabled}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              inputProps={{ maxLength: 250 }}
            />
          )}
        />
      </Box>

      <Controller
        name="reminderScheduledAt"
        control={control}
        render={({ field, fieldState }) => (
          // Desktop variant on purpose: the mobile one opens a clock the
          // hour has to be dialled on, and vets asked to type the time.
          <DesktopDateTimePicker
            label="Data i godzina"
            value={field.value}
            onChange={field.onChange}
            disabled={fieldsDisabled}
            ampm={false}
            minutesStep={1}
            inputFormat="dd.MM.yyyy HH:mm"
            minDateTime={new Date()}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ??
                  translateKey('medication.form.reminderTimeHint')
                }
              />
            )}
          />
        )}
      />

      {singleReminderOnly ? (
        <TextField
          label="Powtarzanie"
          value="Jednorazowo"
          fullWidth
          disabled={fieldsDisabled}
          InputProps={{ readOnly: true }}
          inputProps={{
            'data-testid': 'medication-reminder-recurrence-single',
          }}
        />
      ) : (
        <RHFSelect
          name="reminderRecurrenceType"
          label="Powtarzanie"
          disabled={fieldsDisabled}
          SelectProps={{
            native: false,
            SelectDisplayProps: selectTestId('medication-select-recurrence'),
          }}
        >
          {RECURRENCE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </RHFSelect>
      )}

      {!singleReminderOnly && reminderRecurrenceType ===
        CreateReminderDtoRecurrenceTypeEnum.Custom && (
        <MedicationReminderRecurrence disabled={fieldsDisabled} />
      )}

      <FormControl
        sx={SPAN_FULL}
        error={Boolean(
          errors.reminderEmailEnabled || errors.reminderSmsEnabled,
        )}
      >
        <Typography>{translateKey('hardcoded.rodzaj_powiadomien')}</Typography>
        <FormGroup row>
          <Controller
            name="reminderEmailEnabled"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={fieldsDisabled}
                  />
                }
                label={translateKey('search.book.cardEmail')}
              />
            )}
          />
          <Controller
            name="reminderSmsEnabled"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={fieldsDisabled}
                  />
                }
                label="SMS"
              />
            )}
          />
        </FormGroup>
        {(errors.reminderEmailEnabled || errors.reminderSmsEnabled) && (
          <FormHelperText>
            {translateKey(
              'hardcoded.wybierz_co_najmniej_jeden_kanal_powiadomien',
            )}
          </FormHelperText>
        )}
      </FormControl>
    </>
  );
}
